import React, { useEffect, useRef, useState } from "react";
import { Card, Row, Col, Button, Avatar } from "antd";
import { useUserPermissions } from "../../../UserPermissionsContext";
import Cookies from "js-cookie";
import AudioPlayer from "../audioPlayer";
import { useNavigate } from "react-router-dom";

const barImages = [
    '/assets/img/Vector (1).png', // Bar image 1
    '/assets/img/Vector.png',     // Bar image 2
    '/assets/img/Vector (2).png', // Bar image 3
];

interface Leaderboard {
    rank: number;
    id: number;
    username: string;
    sales_count: number;
    photo: string;
    sl_no: number;
}

interface TopPerformerCardProps {
    user: Leaderboard;
    rank: number;
}


const DigitBox = ({ digit, isAnimating }: { digit: string, isAnimating: boolean }) => {
    return (
        <div style={{
            minWidth: '100px',
            minHeight: '100px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '56px',
            fontWeight: 'bold',
            margin: '0 10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            flexGrow: 1,
            position: 'relative',
            overflow: 'hidden',
        }}>
            <div style={{
                position: 'relative',
                transition: 'transform 0.5s ease-in-out',
                transform: isAnimating ? 'translateY(-10px)' : 'translateY(0)',
                opacity: isAnimating ? 0.6 : 1,
            }}>
                {digit}
            </div>
        </div>
    );
};

const AnimatedNumberDisplay = ({ number, digitsCount }: { number: number, digitsCount: number }) => {
    const [displayNumber, setDisplayNumber] = useState(number);
    const [prevNumber, setPrevNumber] = useState(number);
    const [isAnimating, setIsAnimating] = useState(false);
    const animationTimeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        if (number !== prevNumber) {
            setIsAnimating(true);
            setPrevNumber(number);

            // Clear any existing timeout
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            // Update the displayed number after a short delay
            animationTimeoutRef.current = setTimeout(() => {
                setDisplayNumber(number);
                setIsAnimating(false);
            }, 500); // Match this with your CSS transition duration
        }
    }, [number, prevNumber]);

    // Format the number with leading zeros and split into digits
    const digits = displayNumber.toString().padStart(digitsCount, '0').split('');
    const targetDigits = number.toString().padStart(digitsCount, '0').split('');

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        }}>
            {digits.map((digit, index) => (
                <DigitBox
                    key={index}
                    digit={digit}
                    isAnimating={isAnimating && digit !== targetDigits[index]}
                />
            ))}
        </div>
    );
};

const sampleTop3: Leaderboard[] = [
    {
        id: 1,
        rank: 1,
        username: "Alice",
        sales_count: 150,
        photo: "https://cdn-icons-png.flaticon.com/128/11918/11918393.png",
        sl_no: 1
    },
    {
        id: 2,
        rank: 2,
        username: "Bob",
        sales_count: 120,
        photo: "https://cdn-icons-png.flaticon.com/128/11918/11918393.png",
        sl_no: 2
    },
    {
        id: 3,
        rank: 3,
        username: "Charlie",
        sales_count: 100,
        photo: "https://cdn-icons-png.flaticon.com/128/11918/11918393.png",
        sl_no: 3
    },
];

const TopPerformerCard = ({ user, rank }: { user: any; rank: number }) => {
    const size = 130; // Same size for all profiles
    const rankColors = ["#C0C0C0", "#FFD700", "#CD7F32"];
    const { userPermissions, webApi } = useUserPermissions();

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <div style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '50%',
                overflow: 'hidden',
                border: `3px solid ${rankColors[rank - 1]}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}>
                <img
                    src={
                        user.agent_profile && user.agent_profile.trim() !== ''
                            ? `${webApi}${user.agent_profile}`
                            : 'https://cdn-icons-png.flaticon.com/128/11918/11918393.png'
                    }
                    alt={user.agent_name || 'Agent'}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />

            </div>
            <div style={{ marginTop: '8px', fontWeight: 600 }}>{user.agent_name}</div>
            <div style={{ fontSize: '14px', color: '#555' }}>{user.sales_count} Sales</div>
        </div>
    );
};

const AnimatedTimerDisplay = ({
    seconds
}: {
    seconds: number;
}) => {

    const [displayTime, setDisplayTime] = useState(seconds);
    const [prevTime, setPrevTime] = useState(seconds);
    const [isAnimating, setIsAnimating] = useState(false);

    const animationTimeoutRef =
        useRef<NodeJS.Timeout>();

    useEffect(() => {

        if (seconds !== prevTime) {

            setIsAnimating(true);
            setPrevTime(seconds);

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }

            animationTimeoutRef.current =
                setTimeout(() => {

                    setDisplayTime(seconds);
                    setIsAnimating(false);

                }, 400);
        }

    }, [seconds, prevTime]);

    const formatTime = (totalSeconds: number) => {

        const hrs = Math.floor(totalSeconds / 3600);

        const mins = Math.floor(
            (totalSeconds % 3600) / 60
        );

        const secs = totalSeconds % 60;

        return (
            `${String(hrs).padStart(2, '0')}` +
            `${String(mins).padStart(2, '0')}` +
            `${String(secs).padStart(2, '0')}`
        );
    };

    const displayDigits =
        formatTime(displayTime).split('');

    const targetDigits =
        formatTime(seconds).split('');

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
            }}
        >

            {/* HOURS */}
            <DigitBox
                digit={displayDigits[0]}
                isAnimating={
                    isAnimating &&
                    displayDigits[0] !== targetDigits[0]
                }
            />

            <DigitBox
                digit={displayDigits[1]}
                isAnimating={
                    isAnimating &&
                    displayDigits[1] !== targetDigits[1]
                }
            />

            <div
                style={{
                    fontSize: '64px',
                    fontWeight: 'bold',
                    color: '#22c55e',
                    margin: '0 6px',
                }}
            >
                :
            </div>

            {/* MINUTES */}
            <DigitBox
                digit={displayDigits[2]}
                isAnimating={
                    isAnimating &&
                    displayDigits[2] !== targetDigits[2]
                }
            />

            <DigitBox
                digit={displayDigits[3]}
                isAnimating={
                    isAnimating &&
                    displayDigits[3] !== targetDigits[3]
                }
            />

            <div
                style={{
                    fontSize: '64px',
                    fontWeight: 'bold',
                    color: '#22c55e',
                    margin: '0 6px',
                }}
            >
                :
            </div>

            {/* SECONDS */}
            <DigitBox
                digit={displayDigits[4]}
                isAnimating={
                    isAnimating &&
                    displayDigits[4] !== targetDigits[4]
                }
            />

            <DigitBox
                digit={displayDigits[5]}
                isAnimating={
                    isAnimating &&
                    displayDigits[5] !== targetDigits[5]
                }
            />

        </div>
    );
};

const WizklubSalesDisplay = () => {
    const navigate = useNavigate();
    const [todaySales, setTodaySales] = useState<number>(0);
    const { userPermissions, webApi } = useUserPermissions();
    const [leaderboard, setLeaderBoard] = useState<Leaderboard[]>([]);
    const jwtToken = Cookies.get("authToken");
    const [totalSales, setTotalSales] = useState<number>(0);
    const [weekSales, setWeekSales] = useState<number>(0);
    const [todayTotalTarget, setTodayTarget] = useState<number>(0);
    const [sampleTop3, setSampleTop3] = useState<any[]>([]);
    const [activeTab, setActiveTab] = useState<'Daily' | 'Weekly' | 'Monthly' | 'AllTime'>('Daily');
    const top3 = leaderboard.slice(0, 3);
    const tabMapping: { [key: string]: 'Daily' | 'Weekly' | 'Monthly' | 'AllTime' } = {
        'Today': 'Daily',
        'Last 7 days': 'Weekly',
        'Last 30 days': 'Monthly',
        'AllTime': 'AllTime'
    };
    const others = leaderboard.slice(3);
    const reorderedTop3 = [top3[1], top3[0], top3[2]].filter(Boolean);
    const prevSalesRef = useRef<number>(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const specialAudioRef = useRef<HTMLAudioElement | null>(null);
    const [audioAllowed, setAudioAllowed] = useState(false);
    const playSoundRef = useRef(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const [timerData, setTimerData] = useState<any>(null);
    const [timer, setTimer] = useState(0);
    const timerIntervalRef =
        useRef<NodeJS.Timeout | null>(null);
    const names = [
        "admin", "airtel_webhook", "Akash", "akhila_ms", "Alishala_sandeep", "Avik_thakur",
        "Boggula_Chennakesava", "Chikitha", "Dilip_oraon", "EMP3683", "Ganesh", "Goberu_Abhinav",
        "kumar_rishav", "lokesh_kumar", "madhu", "Mani_kanta", "Manthri_Naveena", "manvitha_c",
        "Mohan_Krishna", "Muzammil_Mashood_Sayyed", "N_Sharon", "Nagma_Begum", "Nandish_E",
        "naveen_nikhil_kumar", "Naveen_Saranam", "pavan", "pavani_jakkula", "Pentapati_Tarun",
        "praneeth", "prasanna_medipelli", "Pushpalatha_Venigalla", "RinshajC", "Rohith_varma",
        "Sai_Chandu", "sales_display", "Sandeep_reddy", "Shailaja", "Sharanshetty", "Sharukh",
        "srilatha", "srilatha_2", "surya_singh", "swarup_vijh", "Syed_Marvan_Uddin", "varna_webhook",
        "yathin", "yuvaraj"
    ];


    useEffect(() => {
        if (totalSales === 9940) {
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), 20000); // hide after 10s
        }
    }, [totalSales]);
    const options = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    };

    useEffect(() => {
        // Load both audio clips
        audioRef.current = new Audio('/sale_sound.mp3');
        specialAudioRef.current = new Audio('/amor-bajo-el-arma-latin-background-music-for-video-full-version-353555.mp3');

        audioRef.current.load();
        specialAudioRef.current.load();

        const enableAudio = () => {
            setAudioAllowed(true);
            if (playSoundRef.current) {
                playAudio();
                playSoundRef.current = false;
            }
            window.removeEventListener('click', enableAudio);
        };

        window.addEventListener('click', enableAudio);

        return () => {
            // Clean up both audio clips
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if (specialAudioRef.current) {
                specialAudioRef.current.pause();
                specialAudioRef.current = null;
            }
            window.removeEventListener('click', enableAudio);
        };
    }, []);

    const playAudio = (isSpecial = false) => {
        const soundRef = isSpecial ? specialAudioRef : audioRef;

        if (!soundRef.current) return;

        soundRef.current.currentTime = 0;
        soundRef.current.play().catch(e => {
            console.log("Audio play failed, will try again after interaction:", e);
            playSoundRef.current = true;
        });
    };


    useEffect(() => {

        timerIntervalRef.current = setInterval(() => {

            setTimer((prev) => prev + 1);

        }, 1000);

        return () => {

            if (timerIntervalRef.current) {

                clearInterval(timerIntervalRef.current);

            }

        };

    }, []);


    useEffect(() => {
        const fetchSalesData = async () => {
            try {
                const response = await fetch(`${webApi}/sales/sales_live_board/`, options);
                const data = await response.json();

                if (data.today_sales_count > prevSalesRef.current) {
                    // Play special sound if count is 237
                    // setTimer(0);
                    if (data.today_sales_count > 10850 && data.today_sales_count < 10853) {
                        playAudio(true); // true means special sound
                    } else {
                        playAudio();
                    }
                }

                setTodaySales(data.today_sales_count);
                setWeekSales(data.week_sales_count);
                setTotalSales(data.total_sales_count);
                setTodayTarget(data.today_sales_target);
                setTimer(data.latest_sale_duration_seconds);
                if (data && data.top_3_agents_this_week) {
                    setSampleTop3(data.top_3_agents_this_week);
                }

                prevSalesRef.current = data.today_sales_count;
            } catch (error) {
                console.error("Error fetching sales data:", error);
            }
        };

        fetchSalesData();
        const salesInterval = setInterval(fetchSalesData, 60000);

        return () => {
            clearInterval(salesInterval);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if (specialAudioRef.current) {
                specialAudioRef.current.pause();
                specialAudioRef.current = null;
            }
        };
    }, []);

    const tabToDays = {
        Daily: "today",
        Weekly: "week",
        Monthly: "month",
        AllTime: "all",
    };

    useEffect(() => {
        setLeaderBoard([]);
        fetchLeaderboard(tabToDays[activeTab]);
    }, [activeTab]);

    const fetchLeaderboard = async (days: string) => {
        const response = await fetch(
            `${webApi}/dashboard/wizklub_sales_leaderboard/?filter=${days}`,
            options
        );
        if (!response.ok) {
            const errorData = await response.json();
            if (errorData.code === "token_not_valid") {
                navigate("/academic/sri-chaitanya/crm/landing-page");
            }
            throw new Error(errorData.detail || "Failed to fetch user data");
        }
        const data = await response.json();
        setLeaderBoard(data);
    };

    useEffect(() => {

        const style = document.createElement('style');

        style.innerHTML = `

    @keyframes gradientMove {

        0% {
            background-position: 0% 50%;
        }

        50% {
            background-position: 100% 50%;
        }

        100% {
            background-position: 0% 50%;
        }
    }

    @keyframes gradientTextMove {

        0% {
            background-position: 0% 50%;
        }

        50% {
            background-position: 100% 50%;
        }

        100% {
            background-position: 0% 50%;
        }
    }

`;

        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };

    }, []);

   const animatedCardStyle = {
    borderRadius: '18px',

    background: `
        linear-gradient(
            270deg,
            #eff6ff,
            #f0fdf4,
            #fefce8,
            #fdf2f8,
            #eef2ff,
            #ecfeff,
            #f5f3ff
        )
    `,

    backgroundSize: '1200% 1200%',

    animation: 'gradientMove 15s ease infinite',

    border: '1px solid #e5e7eb',

    boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
};

    const animatedHeadStyle = {
        fontSize: '20px',
        padding: '6px 12px',
        background: 'transparent',
        textAlign: 'center' as const,
        fontWeight: 700,
    };

    // const blueGradient = {
    //     background:
    //         'linear-gradient(270deg, #dbeafe, #bfdbfe, #93c5fd, #60a5fa)',
    // };

    // const greenGradient = {
    //     background:
    //         'linear-gradient(270deg, #dcfce7, #bbf7d0, #86efac, #4ade80)',
    // };

    // const purpleGradient = {
    //     background:
    //         'linear-gradient(270deg, #ede9fe, #ddd6fe, #c4b5fd, #a78bfa)',
    // };

    // const pinkGradient = {
    //     background:
    //         'linear-gradient(270deg, #fce7f3, #fbcfe8, #f9a8d4, #f472b6)',
    // };

    return (
        <div style={{
            padding: '12px',
            backgroundColor: '#f5f5f5',
            minHeight: '100vh',
            boxSizing: 'border-box'
        }}>
            <Row gutter={[24, 24]} style={{ margin: 0 }}>
                <Col xs={24} md={24} style={{
                    padding: '0 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}>
                    <Card
                        style={{
                            borderRadius: '10px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            margin: 0
                        }}
                        headStyle={{
                            fontSize: '32px',
                            fontWeight: 600,
                            padding: '10px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            textAlign: 'center'
                        }}
                        bodyStyle={{
                            padding: '12px',
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '12px',
                            alignItems: "center"
                        }}
                    >
                        {/* Top Row */}
                        {/* Top Row: Today Sales & Total Sales side by side */}
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Card
                                    type="inner"
                                    title="Sales Floor"
                                    style={{
                                        ...animatedCardStyle,
                                        // ...blueGradient,
                                        height: "230px",
                                    }}
                                     headStyle={animatedHeadStyle}
                                    bodyStyle={{
                                        padding: '8px',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <AnimatedNumberDisplay number={todaySales} digitsCount={3} />
                                </Card>
                            </Col>

                            <Col xs={24} md={12}>
                                <Card
                                    type="inner"
                                    title="Target"
                                    style={{
                                        ...animatedCardStyle,
                                        // ...greenGradient,
                                        height: "230px",
                                    }}
                                     headStyle={animatedHeadStyle}
                                    bodyStyle={{
                                        padding: '8px',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <AnimatedNumberDisplay number={todayTotalTarget} digitsCount={3} />
                                </Card>
                            </Col>
                        </Row>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                margin: '20px 0',
                            }}
                        >

                            <Card
                                style={{
                                    borderRadius: '18px',

                                    background:
                                        'linear-gradient(270deg, #dbeafe, #e0f2fe, #dcfce7, #fef9c3, #fce7f3)',

                                    backgroundSize: '1000% 1000%',

                                    animation: 'gradientMove 12s ease infinite',

                                    minWidth: '650px',

                                    border: '1px solid #dbeafe',

                                    boxShadow:
                                        '0 6px 18px rgba(0,0,0,0.08)',
                                }}
                                bodyStyle={{
                                    padding: '24px',
                                }}
                            >

                                <div
                                    style={{
                                        textAlign: 'center',

                                        fontSize: '24px',

                                        fontWeight: 800,

                                        marginBottom: '20px',

                                        background:
                                            'linear-gradient(270deg, #2563eb, #0f766e, #9333ea, #db2777)',

                                        backgroundSize: '600% 600%',

                                        WebkitBackgroundClip: 'text',

                                        WebkitTextFillColor: 'transparent',

                                        animation: 'gradientTextMove 8s ease infinite',

                                        letterSpacing: '1px',
                                    }}
                                >
                                    Time Since Last Sale
                                </div>

                                <AnimatedTimerDisplay
                                    seconds={timer}
                                />

                            </Card>

                        </div>
                        {/* Bottom Row */}
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Card
                                    type="inner"
                                    title="Weekly Sales"
                                    style={{
                                        ...animatedCardStyle,
                                            // ...purpleGradient,
                                        height: "330px",
                                    }}
                                    headStyle={animatedHeadStyle}
                                    bodyStyle={{
                                        padding: '8px',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <AnimatedNumberDisplay number={weekSales} digitsCount={5} />
                                </Card>
                            </Col>

                            <Col xs={24} md={12}>
                                <Card
                                    type="inner"
                                    title="Top 3 Performers"
                                    style={{
                                        ...animatedCardStyle,
                                        // ...pinkGradient,
                                        height: "330px",
                                    }}
                                     headStyle={animatedHeadStyle}
                                    bodyStyle={{
                                        padding: '8px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-around',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        height: '100%',
                                        gap: '24px',
                                        transform: 'translateY(-62px)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '12px 0',
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'flex-end',
                                                gap: '56px',
                                                width: '100%',
                                                maxWidth: '500px',
                                            }}>
                                                {/* 2nd Place */}
                                                {sampleTop3[0] && (
                                                    <TopPerformerCard user={sampleTop3[0]} rank={2} />
                                                )}

                                                {/* 1st Place */}
                                                {sampleTop3[1] && (
                                                    <TopPerformerCard user={sampleTop3[1]} rank={1} />
                                                )}

                                                {/* 3rd Place */}
                                                {sampleTop3[2] && (
                                                    <TopPerformerCard user={sampleTop3[2]} rank={3} />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>

    );
};

export default WizklubSalesDisplay;